import { useEffect, useState } from 'react';
import useSWR from 'swr';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';

type ProductRow = {
  id: string;
  displayName?: string;
  productTitle?: string;
  sortOrder?: number;
  active?: boolean;
};
type ProductsPayload = { products: ProductRow[] };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: number;
  currentProductId?: string;
  busy?: boolean;
  onConfirm: (newProductId: string) => void | Promise<void>;
};

function productLabel(p: ProductRow): string {
  return p.displayName || p.productTitle || p.id;
}

export function EditProductDialog({ open, onOpenChange, tier, currentProductId, busy, onConfirm }: Props) {
  // Prize submissions use the prizeProducts catalog (gray-jacket-left-chest…),
  // NOT the team-store productConfigs — /api/products here offered the wrong
  // catalog entirely (found 2026-06-10). Order mirrors the New Submission
  // dropdown (sortOrder, then name).
  const { data, isLoading } = useSWR<ProductsPayload>(open ? '/api/prize-products' : null, get);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (open) setSelected(currentProductId ?? '');
  }, [open, currentProductId]);

  const products = (data?.products ?? [])
    .filter((p) => p.active !== false)
    .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || String(productLabel(a)).localeCompare(productLabel(b)));
  const newLabel = products.find((p) => p.id === selected);
  const changed = !!selected && selected !== currentProductId;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change product {tier}?</DialogTitle>
          <DialogDescription>
            This will clear the existing mockup and preflight result. Submission status
            will be set to <code>changes_requested</code>. A new mockup will be requested.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <label className="block text-[11px] uppercase tracking-[0.04em] text-muted">New product</label>
          {isLoading ? (
            <Skeleton className="mt-2 h-9 w-full" />
          ) : (
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={busy}
              className="mt-2 w-full rounded-sm border border-border bg-surface px-2 py-1.5 text-[12px]"
            >
              <option value="">— select —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{productLabel(p)}</option>
              ))}
            </select>
          )}
          {currentProductId && (
            <p className="mt-2 text-[11px] text-muted">Current: <code>{currentProductId}</code></p>
          )}
          {changed && newLabel && (
            <p className="mt-1 text-[11px] text-text">New: <code>{selected}</code> ({productLabel(newLabel)})</p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            disabled={busy || !changed}
            onClick={() => { void onConfirm(selected); }}
          >
            {busy ? 'Saving…' : 'Change product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
